import { useEffect, useState } from "react";
import { api, fmtDate, todayISO } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useFilters } from "@/context/FiltersContext";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Plus, LayoutGrid, Table, Paperclip, UploadCloud, Loader2 } from "lucide-react";

const STAGES = ["Contacted", "In Discussion", "Agreement Signed", "Active/Referring"];

const emptyForm = {
  date: todayISO(), executive_id: "", tieup_type: "Pharmacy",
  contact_person: "", organisation_name: "", contact_number: "", address: "",
  agreement_signed: "In Discussion", first_referral_date: "", follow_up_date: "",
  remarks: "", stage: "Contacted",
};

export default function TieUps() {
  const { user } = useAuth();
  const { executives, selectedExecId } = useFilters();
  const [tieups, setTieups] = useState([]);
  const [view, setView] = useState("kanban");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [editing, setEditing] = useState(null);
  const [uploadFor, setUploadFor] = useState(null);
  const [saving, setSaving] = useState(false);
  const [activeTypes, setActiveTypes] = useState([]);

  const load = () => {
    const params = {};
    if (user?.role !== "executive" && selectedExecId !== "all") params.executive_id = selectedExecId;
    api.get("/tieups", { params }).then((r) => setTieups(r.data));
  };
  useEffect(load, [user, selectedExecId]);

  useEffect(() => {
    api.get("/settings/categories", { params: { active_only: true } })
      .then((r) => setActiveTypes(r.data.map((c) => c.name)))
      .catch(() => setActiveTypes([]));
  }, []);

  const openNew = () => {
    setEditing(null);
    setForm({ ...emptyForm, executive_id: user.role === "executive" ? user.executive_id : (executives[0]?.id || "") });
    setOpen(true);
  };
  const openEdit = (t) => {
    setEditing(t);
    setForm({
      date: t.date, executive_id: t.executive_id, tieup_type: t.tieup_type,
      contact_person: t.contact_person, organisation_name: t.organisation_name,
      contact_number: t.contact_number || "", address: t.address || "",
      agreement_signed: t.agreement_signed, first_referral_date: t.first_referral_date || "",
      follow_up_date: t.follow_up_date || "", remarks: t.remarks || "", stage: t.stage,
    });
    setOpen(true);
  };
  const submit = async () => {
    if (!form.executive_id || !form.contact_person || !form.organisation_name) {
      toast.error("Fill executive, contact person and organisation"); return;
    }
    const payload = {
      ...form,
      first_referral_date: form.first_referral_date || null,
      follow_up_date: form.follow_up_date || null,
    };
    setSaving(true);
    try {
      if (editing) await api.put(`/tieups/${editing.id}`, payload);
      else await api.post("/tieups", payload);
      toast.success("Saved");
      setOpen(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save");
    } finally { setSaving(false); }
  };

  const updateStage = async (t, stage) => {
    try {
      await api.patch(`/tieups/${t.id}/stage`, null, { params: { stage } });
      load();
    } catch { toast.error("Stage update failed"); }
  };

  const execName = (id) => executives.find((e) => e.id === id)?.name || "—";

  const grouped = STAGES.reduce((acc, s) => ({ ...acc, [s]: tieups.filter((t) => t.stage === s) }), {});
  const today = todayISO();

  return (
    <div className="space-y-6" data-testid="tieups-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="text-[10px] tracking-[0.25em] uppercase text-[#C9A24B]">Partner CRM</div>
          <h2 className="text-3xl font-light text-white mt-1" style={{ fontFamily: "Outfit" }}>
            Tie-Up Pipeline
          </h2>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex bg-slate-800/60 border border-slate-700 rounded-lg overflow-hidden">
            <button data-testid="tieups-view-kanban" onClick={() => setView("kanban")}
              className={`px-3 py-1.5 text-sm flex items-center gap-1.5 ${view === "kanban" ? "bg-[#C9A24B] text-[#0F172A]" : "text-slate-300"}`}>
              <LayoutGrid size={14} /> Kanban
            </button>
            <button data-testid="tieups-view-table" onClick={() => setView("table")}
              className={`px-3 py-1.5 text-sm flex items-center gap-1.5 ${view === "table" ? "bg-[#C9A24B] text-[#0F172A]" : "text-slate-300"}`}>
              <Table size={14} /> Table
            </button>
          </div>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <button data-testid="btn-add-tieup" onClick={openNew}
                className="bg-[#C9A24B] hover:bg-[#b08d40] text-[#0F172A] font-semibold rounded-lg px-4 py-2 text-sm flex items-center gap-2">
                <Plus size={16} /> Add Tie-Up
              </button>
            </DialogTrigger>
            <DialogContent className="bg-[#0F172A] border-slate-700 text-white max-w-2xl">
              <DialogHeader><DialogTitle>{editing ? "Edit Tie-Up" : "New Tie-Up"}</DialogTitle></DialogHeader>
              <div className="grid grid-cols-2 gap-3">
                {user?.role !== "executive" && (
                  <div className="col-span-2">
                    <label className="text-xs text-slate-400">Executive</label>
                    <Select value={form.executive_id} onValueChange={(v) => setForm({ ...form, executive_id: v })}>
                      <SelectTrigger data-testid="tieup-exec" className="bg-slate-900 border-slate-700 mt-1"><SelectValue placeholder="Choose executive" /></SelectTrigger>
                      <SelectContent className="bg-[#0F172A] border-slate-700 text-white">
                        {executives.map((e) => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                <div><label className="text-xs text-slate-400">Date</label>
                  <input data-testid="tieup-date" type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })}
                    className="mt-1 w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-1.5 text-sm outline-none" /></div>
                <div>
                  <label className="text-xs text-slate-400">Tie-Up Type</label>
                  <Select value={form.tieup_type} onValueChange={(v) => setForm({ ...form, tieup_type: v })}>
                    <SelectTrigger data-testid="tieup-type" className="bg-slate-900 border-slate-700 mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[#0F172A] border-slate-700 text-white">
                      {/* When editing, ensure the record's original type remains available even if now inactive */}
                      {(editing && form.tieup_type && !activeTypes.includes(form.tieup_type)
                        ? [form.tieup_type, ...activeTypes]
                        : activeTypes
                      ).map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div><label className="text-xs text-slate-400">Contact Person</label>
                  <input data-testid="tieup-contact" value={form.contact_person} onChange={(e) => setForm({ ...form, contact_person: e.target.value })}
                    className="mt-1 w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-1.5 text-sm outline-none" /></div>
                <div><label className="text-xs text-slate-400">Organisation</label>
                  <input data-testid="tieup-org" value={form.organisation_name} onChange={(e) => setForm({ ...form, organisation_name: e.target.value })}
                    className="mt-1 w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-1.5 text-sm outline-none" /></div>
                <div><label className="text-xs text-slate-400">Contact Number</label>
                  <input data-testid="tieup-phone" value={form.contact_number} onChange={(e) => setForm({ ...form, contact_number: e.target.value })}
                    className="mt-1 w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-1.5 text-sm outline-none" /></div>
                <div><label className="text-xs text-slate-400">Address / Area</label>
                  <input data-testid="tieup-addr" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })}
                    className="mt-1 w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-1.5 text-sm outline-none" /></div>
                <div>
                  <label className="text-xs text-slate-400">Agreement Signed</label>
                  <Select value={form.agreement_signed} onValueChange={(v) => setForm({ ...form, agreement_signed: v })}>
                    <SelectTrigger className="bg-slate-900 border-slate-700 mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[#0F172A] border-slate-700 text-white">
                      <SelectItem value="Yes">Yes</SelectItem>
                      <SelectItem value="No">No</SelectItem>
                      <SelectItem value="In Discussion">In Discussion</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-xs text-slate-400">Stage</label>
                  <Select value={form.stage} onValueChange={(v) => setForm({ ...form, stage: v })}>
                    <SelectTrigger data-testid="tieup-stage" className="bg-slate-900 border-slate-700 mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[#0F172A] border-slate-700 text-white">
                      {STAGES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div><label className="text-xs text-slate-400">First Referral Date</label>
                  <input type="date" value={form.first_referral_date} onChange={(e) => setForm({ ...form, first_referral_date: e.target.value })}
                    className="mt-1 w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-1.5 text-sm outline-none" /></div>
                <div><label className="text-xs text-slate-400">Follow-Up Date</label>
                  <input type="date" value={form.follow_up_date} onChange={(e) => setForm({ ...form, follow_up_date: e.target.value })}
                    className="mt-1 w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-1.5 text-sm outline-none" /></div>
                <div className="col-span-2"><label className="text-xs text-slate-400">Remarks</label>
                  <textarea value={form.remarks} onChange={(e) => setForm({ ...form, remarks: e.target.value })}
                    className="mt-1 w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-1.5 text-sm outline-none" rows={2} /></div>
              </div>
              <DialogFooter>
                <button data-testid="tieup-save" onClick={submit} disabled={saving}
                  className="bg-[#C9A24B] hover:bg-[#b08d40] text-[#0F172A] font-semibold rounded-lg px-4 py-2 flex items-center gap-2">
                  {saving && <Loader2 size={14} className="animate-spin" />} Save
                </button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {view === "kanban" ? (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {STAGES.map((stage) => (
            <div key={stage} className="bg-[#0B1120] border border-slate-800 rounded-xl overflow-hidden"
              data-testid={`kanban-col-${stage.replace(/\W/g, "-").toLowerCase()}`}>
              <div className="px-4 py-3 border-b border-slate-800 bg-slate-800/40 flex items-center justify-between">
                <div className="text-xs uppercase tracking-widest text-slate-300 font-semibold">{stage}</div>
                <div className="text-xs text-[#C9A24B] font-bold">{grouped[stage].length}</div>
              </div>
              <div className="p-3 space-y-2 min-h-[180px] max-h-[600px] overflow-y-auto">
                {grouped[stage].map((t) => {
                  const overdue = t.follow_up_date && t.follow_up_date < today && stage !== "Active/Referring";
                  return (
                    <div key={t.id} onClick={() => openEdit(t)}
                      className={`bg-[#1E293B] hover:bg-slate-800 rounded-lg p-3 border cursor-pointer transition-colors ${overdue ? "border-[#DC2626]/40" : "border-slate-700/50"}`}>
                      <div className="text-sm font-medium text-white">{t.organisation_name}</div>
                      <div className="text-xs text-slate-400 mt-0.5">{t.contact_person} • {t.tieup_type}</div>
                      <div className="flex items-center justify-between mt-2 text-[11px]">
                        <span className="text-slate-500">{execName(t.executive_id)}</span>
                        {overdue && <span className="text-[#DC2626] font-semibold">OVERDUE</span>}
                      </div>
                      <div className="flex gap-1 mt-2">
                        {STAGES.filter((s) => s !== stage).map((s) => (
                          <button key={s} onClick={(e) => { e.stopPropagation(); updateStage(t, s); }}
                            className="text-[10px] text-slate-400 hover:text-[#C9A24B] hover:bg-slate-900 rounded px-1.5 py-0.5">
                            → {s.split(" ")[0]}
                          </button>
                        ))}
                      </div>
                      {t.attachments?.length > 0 && (
                        <div className="text-[10px] text-slate-400 mt-1.5 flex items-center gap-1">
                          <Paperclip size={10} /> {t.attachments.length} file(s)
                        </div>
                      )}
                      <button onClick={(e) => { e.stopPropagation(); setUploadFor(t); }}
                        className="mt-2 text-[10px] text-[#C9A24B] hover:underline flex items-center gap-1">
                        <UploadCloud size={11} /> Attach file
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="bg-[#1E293B] rounded-xl border border-slate-800 overflow-hidden" data-testid="tieups-table">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-800/50 text-slate-300 text-[11px] uppercase tracking-wider">
                  {["Date", "Executive", "Type", "Contact", "Organisation", "Phone", "Agreement", "Follow-Up", "Stage"].map((h) => (
                    <th key={h} className="text-left px-3 py-3 font-semibold">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {tieups.map((t) => (
                  <tr key={t.id} onClick={() => openEdit(t)} className="border-t border-slate-800 hover:bg-slate-800/30 cursor-pointer">
                    <td className="px-3 py-2.5">{fmtDate(t.date)}</td>
                    <td className="px-3 py-2.5">{execName(t.executive_id)}</td>
                    <td className="px-3 py-2.5">{t.tieup_type}</td>
                    <td className="px-3 py-2.5">{t.contact_person}</td>
                    <td className="px-3 py-2.5 text-white font-medium">{t.organisation_name}</td>
                    <td className="px-3 py-2.5">{t.contact_number}</td>
                    <td className="px-3 py-2.5">{t.agreement_signed}</td>
                    <td className={`px-3 py-2.5 ${t.follow_up_date && t.follow_up_date < today ? "text-[#DC2626] font-semibold" : ""}`}>
                      {fmtDate(t.follow_up_date)}
                    </td>
                    <td className="px-3 py-2.5"><span className="text-[#C9A24B]">{t.stage}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <UploadDialog target={uploadFor} onClose={() => { setUploadFor(null); load(); }} kind="tieup" />
    </div>
  );
}

export function UploadDialog({ target, onClose, kind }) {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  if (!target) return null;
  const submit = async () => {
    if (!file) { toast.error("Choose file"); return; }
    const fd = new FormData();
    fd.append("file", file);
    fd.append("related_type", kind);
    fd.append("related_id", target.id);
    setUploading(true);
    try {
      await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("File uploaded");
      onClose();
    } catch { toast.error("Upload failed"); }
    finally { setUploading(false); }
  };
  return (
    <Dialog open={!!target} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#0F172A] border-slate-700 text-white">
        <DialogHeader><DialogTitle>Attach file to {target.organisation_name || target.name}</DialogTitle></DialogHeader>
        <input data-testid="upload-file" type="file" onChange={(e) => setFile(e.target.files?.[0])}
          className="text-sm text-slate-200 file:bg-[#C9A24B] file:text-[#0F172A] file:font-semibold file:rounded-lg file:border-0 file:px-3 file:py-1.5 file:mr-3" />
        <DialogFooter>
          <button data-testid="upload-submit" onClick={submit} disabled={uploading}
            className="bg-[#C9A24B] hover:bg-[#b08d40] text-[#0F172A] font-semibold rounded-lg px-4 py-2 flex items-center gap-2">
            {uploading && <Loader2 size={14} className="animate-spin" />} Upload
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
